import classNames from "classnames";
import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import { MediaPreviewPlaceholder } from "./MediaPreviewPlaceholder";
import { getMediaComposition, objectFitByComposition, type MediaComposition } from "./types";
import { useImageLoadState } from "./useImageLoadState";

export interface MediaPreviewImageProps {
  src?: string;
  alt?: string;
  className?: string;
  imageClassName?: string;
  onLoadFailedChange?: (failed: boolean) => void;
}

export const MediaPreviewImage = ({
  src,
  alt = "",
  className,
  imageClassName,
  onLoadFailedChange,
}: MediaPreviewImageProps) => {
  const [composition, setComposition] = useState<MediaComposition>("landscapeOrSquare");
  const { imgRef, hasError, onLoad, onError } = useImageLoadState(src);

  useEffect(() => {
    if (!src) {
      onLoadFailedChange?.(false);
      return;
    }
    onLoadFailedChange?.(hasError);
  }, [src, hasError, onLoadFailedChange]);

  const handleLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      onLoad(event);
      const { naturalWidth, naturalHeight } = event.currentTarget;
      setComposition(getMediaComposition(naturalWidth, naturalHeight));
    },
    [onLoad],
  );

  if (!src) {
    return <MediaPreviewPlaceholder className={classNames("h-full w-full", className)} />;
  }

  return (
    <div className={classNames("relative h-full w-full", className)}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={classNames(
          "pointer-events-none h-full w-full",
          objectFitByComposition[composition],
          hasError && "hidden",
          imageClassName,
        )}
        onLoad={handleLoad}
        onError={onError}
      />
      {hasError ? <MediaPreviewPlaceholder className="absolute inset-0" /> : null}
    </div>
  );
};
